/**
 * Spoken updates on the client: my server-stored preferences (GET/PUT /api/me/callout-prefs) and the
 * callouts that arrived for me over the mission socket (the server decides what to say and when).
 */
import { create } from 'zustand';
import { DEFAULT_CALLOUT_PREFS, type Callout, type CalloutPrefs } from '@apos/shared';
import { api, errorMessage } from '../lib/api';

export interface ReceivedCallout extends Callout {
  receivedAt: number;
}

const KEEP = 20;

interface CalloutStore {
  prefs: CalloutPrefs | null;
  error: string | null;
  recent: ReceivedCallout[];
  load(): Promise<void>;
  update(patch: Partial<CalloutPrefs>): Promise<void>;
  /** A callout from the socket; returns false for a duplicate id. */
  receive(c: Callout, receivedAt?: number): boolean;
  clear(): void;
}

export const useCallouts = create<CalloutStore>((set, get) => ({
  prefs: null,
  error: null,
  recent: [],
  async load() {
    try {
      set({ prefs: await api.calloutPrefs.get(), error: null });
    } catch (e) {
      set({ error: errorMessage(e) });
    }
  },
  async update(patch) {
    const before = get().prefs ?? { ...DEFAULT_CALLOUT_PREFS };
    set({ prefs: { ...before, ...patch }, error: null }); // optimistic: the switch moves at once
    try {
      set({ prefs: await api.calloutPrefs.set(patch) });
    } catch (e) {
      set({ prefs: before, error: errorMessage(e) });
    }
  },
  receive(c, receivedAt = Date.now()) {
    if (get().recent.some((x) => x.id === c.id)) return false;
    set((s) => ({ recent: [{ ...c, receivedAt }, ...s.recent].slice(0, KEEP) }));
    return true;
  },
  clear: () => set({ recent: [] }),
}));
